/* @jsx jsx */
import FocusLock from 'react-focus-lock';
import { useOverlay } from '@react-aria/overlays';
import { jsx } from '@theme-ui/core';
import * as React from 'react';

interface PopoverProps {
  popoverRef?: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
  isOpen?: boolean;
  onClose: () => void;
  onClickOutPopup?: () => void;
  className?: string;
  autoFocus?: boolean;
  updateAction?: () => void;
}

export function Popover(props: PopoverProps) {
  let ref = React.useRef<HTMLDivElement>(null);
  let {
    popoverRef = ref,
    isOpen,
    onClose,
    children,
    autoFocus = true,
    updateAction = () => {},
    ...rest
  } = props;

  // Handle events that should cause the popup to close,
  // e.g. blur, clicking outside, or pressing the escape key.
  let { overlayProps } = useOverlay(
    {
      isOpen,
      onClose,
      isDismissable: true,
      shouldCloseOnInteractOutside: (e) => {
        if (e.tagName.toLowerCase() === 'html') {
          return false;
        }
        if (props.onClickOutPopup) {
          props.onClickOutPopup();
        }
        return true;
      }
    },
    popoverRef
  );

  // Add a hidden <DismissButton> component at the end of the popover
  // to allow screen reader users to dismiss the popup easily.
  return (
    <FocusLock
      returnFocus
      autoFocus={autoFocus}
      onActivation={() => {
        window.setTimeout(() => {
          updateAction();
        }, 250);
      }}
    >
      <div
        {...overlayProps}
        sx={{ position: 'absolute', zIndex: 1, overflow: 'auto', variant: 'layout.popover' }}
        {...rest}
        ref={popoverRef}
      >
        {children}
      </div>
    </FocusLock>
  );
}
